import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Check, X, Inbox } from "lucide-react";
import { toast } from "sonner";
import { formatMinutes, formatDecimalHours, formatWeekLabel } from "@/lib/timeFormat";
import ExportCsvButton from "@/components/ExportCsvButton";
import StatusPill from "@/components/hours/StatusPill";

export default function PendingQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // { id: true }
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [pendingAction, setPendingAction] = useState(null); // { type, ids }
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals/pending");
      setItems(data.items);
      setSelected({});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const allChecked = items.length > 0 && selectedIds.length === items.length;
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(items.map((i) => [i.id, true])));
  };
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const approveOne = async (id) => {
    setBusy(true);
    try {
      await api.post(`/approvals/timesheets/${id}/approve`, {});
      toast.success("Approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const openReject = (ids) => {
    setPendingAction({ type: "reject", ids });
    setRejectReason("");
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    if (!rejectReason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      if (pendingAction.ids.length === 1) {
        await api.post(`/approvals/timesheets/${pendingAction.ids[0]}/reject`, { reason: rejectReason });
      } else {
        await api.post("/approvals/bulk-reject", { timesheet_ids: pendingAction.ids, reason: rejectReason });
      }
      toast.success(`${pendingAction.ids.length} rejected`);
      setRejectOpen(false);
      setPendingAction(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const bulkApprove = async () => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/approvals/bulk-approve", { timesheet_ids: selectedIds });
      toast.success(`${data.approved} approved`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="approvals-pending-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Approvals</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Pending queue</h1>
        <p className="mt-2 text-muted-foreground">Timesheets waiting on your approval. Bulk actions are at the top of the table.</p>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between rounded-lg border bg-accent/40 p-3" data-testid="bulk-bar">
          <div className="text-sm">
            <strong>{selectedIds.length}</strong> selected
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={bulkApprove} disabled={busy} data-testid="bulk-approve-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve selected
            </Button>
            <Button size="sm" variant="destructive" onClick={() => openReject(selectedIds)} disabled={busy} data-testid="bulk-reject-btn">
              <X className="h-4 w-4" /> Reject selected
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Pending timesheets ({items.length})</CardTitle>
          <ExportCsvButton
            endpoint="tenant/timesheets/export.csv"
            filename="quanta-pending-timesheets.csv"
            filters={{ status: "submitted" }}
            testId="export-csv-pending"
          />
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all" />
                </TableHead>
                <TableHead>Worker</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-16 text-center">
                  <div className="mx-auto flex max-w-sm flex-col items-center gap-3 text-muted-foreground">
                    <Inbox className="h-10 w-10 opacity-30" />
                    <div className="text-sm">No timesheets waiting. When someone submits, you'll see them here.</div>
                  </div>
                </TableCell></TableRow>
              )}
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`pending-row-${it.id}`} className="cursor-pointer" onClick={(e) => {
                  if (e.target.closest("button, input, [role=checkbox]")) return;
                  nav(`/app/approvals/${it.id}`);
                }}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={!!selected[it.id]} onCheckedChange={() => toggle(it.id)} data-testid={`select-${it.id}`} />
                  </TableCell>
                  <TableCell className="font-medium">{it.worker_name}</TableCell>
                  <TableCell>{formatWeekLabel(it.period_start_local, it.period_end_local)}</TableCell>
                  <TableCell className="tabular-nums">
                    {formatMinutes(it.total_minutes)} <span className="text-muted-foreground">({formatDecimalHours(it.total_minutes)}h)</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{it.submitted_at ? new Date(it.submitted_at).toLocaleString() : "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <StatusPill status={it.status || "submitted"} size="sm" />
                      <Badge variant="outline" className="rounded-full text-[10px] uppercase">{it.submission_source || "worker"}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => approveOne(it.id)} disabled={busy} data-testid={`approve-${it.id}`}>
                        <Check className="h-4 w-4 text-success" /> Approve
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openReject([it.id])} disabled={busy} data-testid={`reject-${it.id}`}>
                        <X className="h-4 w-4 text-destructive" /> Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={rejectOpen} onOpenChange={(v) => !v && setRejectOpen(false)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject {pendingAction?.ids?.length === 1 ? "timesheet" : `${pendingAction?.ids?.length} timesheets`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="reason">Reason (required)</Label>
            <Textarea id="reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={4} placeholder="Explain what needs to change…" data-testid="reject-reason" />
            <Alert>
              <AlertDescription>The worker will get an email with the reason and a link to edit + resubmit.</AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmReject} disabled={busy || !rejectReason.trim()} data-testid="reject-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
